'use client';

import Box from '@mui/material/Box';
import List from '@mui/material/List';

import NavLinkItem from '@/components/NavLinkItem';
import { type NavLinkWithChildren, hasNavLinkChildren, navLinks } from '@/config/navigation.config';
import { usePathname } from '@/i18n/navigation';

import NavDropdown from './NavDropdown';
import NavDropdownPanel from './NavDropdown/NavDropdownPanel';
import styles from './Navigation.module.scss';

export interface NavigationProps {
  openLink: NavLinkWithChildren | null;
  panelLink: NavLinkWithChildren | null;
  isPanelOpen: boolean;
  isPanelClosing: boolean;
  onOpen: (link: NavLinkWithChildren) => void;
  onClose: () => void;
  onPanelMouseEnter: () => void;
  onPanelMouseLeave: () => void;
  onCloseImmediate: () => void;
  onPanelCloseComplete: () => void;
}

const Navigation = ({
  openLink,
  panelLink,
  isPanelOpen,
  isPanelClosing,
  onOpen,
  onClose,
  onPanelMouseEnter,
  onPanelMouseLeave,
  onCloseImmediate,
  onPanelCloseComplete,
}: NavigationProps) => {
  const pathname = usePathname();
  const isPanelVisible = panelLink && (isPanelOpen || isPanelClosing);

  return (
    <Box component="nav" className={styles.nav}>
      <List classes={{ root: styles.root }} className={styles.navList} disablePadding>
        {navLinks.map(link => {
          if (hasNavLinkChildren(link)) {
            return (
              <NavDropdown
                key={link.href}
                link={link}
                open={openLink?.href === link.href}
                onOpen={() => onOpen(link)}
                onClose={onClose}
              />
            );
          }

          const isActive = pathname === link.href || pathname.startsWith(`${link.href}/`);

          return <NavLinkItem key={link.href} href={link.href} labelKey={link.labelKey} isActive={isActive} />;
        })}
      </List>
      {isPanelVisible && (
        <NavDropdownPanel
          link={panelLink}
          open={isPanelOpen}
          onMouseEnter={onPanelMouseEnter}
          onMouseLeave={onPanelMouseLeave}
          onClick={onCloseImmediate}
          onCloseComplete={onPanelCloseComplete}
        />
      )}
    </Box>
  );
};

export default Navigation;
